/**
 * Ce que la page du lien de pointage et l'écran de gestion lisent et écrivent
 * (migration `20261031200000_le_pointage_de_la_garderie`).
 *
 * Côté animateur, tout passe par le jeton de l'adresse, sans compte : la base
 * vérifie le lien à chaque appel. Côté gestion, la création et la révocation
 * sont des fonctions réservées à l'administration et à la coordination ; la
 * liste des liens se lit dans la table, que la même règle protège.
 */
import { db } from '../payments/db';
import { lireFiche, lireLecture, type LectureDeFiche, type LectureDuLien, type ResultatDuPointage, type SourceDuPointage } from './pointage';

export type { ResultatDuPointage } from './pointage';

/* ------------------------------------------------------------------------ */
/*  Le téléphone de l'animateur                                              */
/* ------------------------------------------------------------------------ */

export async function lireLePointage(jeton: string, code: string | null, date: string | null): Promise<LectureDuLien> {
  const { data, error } = await db.rpc('garderie_lien_lire', { p_jeton: jeton, p_code: code, p_date: date });
  if (error) throw error;
  return lireLecture(data);
}

export async function pointer(
  jeton: string,
  code: string | null,
  enfantId: string,
  date: string,
  plageId: string,
  present: boolean,
  prenom: string | null,
): Promise<ResultatDuPointage> {
  const { data, error } = await db.rpc('garderie_lien_pointer', {
    p_jeton: jeton,
    p_code: code,
    p_enfant_id: enfantId,
    p_date: date,
    p_plage_id: plageId,
    p_present: present,
    p_prenom: prenom,
  });
  if (error) throw error;
  const d = (data ?? {}) as { code_requis?: boolean; present?: boolean };
  if (d.code_requis) return { codeRequis: true };
  return { codeRequis: false, present: d.present === true };
}

export async function lireLaFiche(jeton: string, code: string | null, enfantId: string): Promise<LectureDeFiche> {
  const { data, error } = await db.rpc('garderie_lien_enfant', { p_jeton: jeton, p_code: code, p_enfant_id: enfantId });
  if (error) throw error;
  return lireFiche(data);
}

/** Le téléphone de l'animateur : tout passe par le jeton, le code et le prénom donné à l'ouverture. */
export function sourceDuLien(jeton: string, code: string | null, prenom: string): SourceDuPointage {
  return {
    cle: jeton,
    lire: (date) => lireLePointage(jeton, code, date),
    pointer: (enfantId, date, plageId, present) => pointer(jeton, code, enfantId, date, plageId, present, prenom),
    lireFiche: (enfantId) => lireLaFiche(jeton, code, enfantId),
  };
}

/* ------------------------------------------------------------------------ */
/*  La gestion                                                               */
/* ------------------------------------------------------------------------ */

export interface EcoleDeGarderie {
  centerId: string;
  nom: string;
  code: string;
  anneeScolaire: number;
}

/** Les écoles qui ont un catalogue de garderie : celles pour lesquelles un lien se crée. */
export async function lireLesEcoles(): Promise<EcoleDeGarderie[]> {
  const { data, error } = await db
    .from('garderie_annees')
    .select('center_id, annee_scolaire, centers(name, tag)')
    .order('annee_scolaire', { ascending: false });
  if (error) throw error;
  const vues = new Set<string>();
  const out: EcoleDeGarderie[] = [];
  for (const l of (data ?? []) as unknown as { center_id: string; annee_scolaire: number; centers: { name: string; tag: string } | { name: string; tag: string }[] | null }[]) {
    if (vues.has(l.center_id)) continue;
    vues.add(l.center_id);
    const c = Array.isArray(l.centers) ? l.centers[0] : l.centers;
    out.push({ centerId: l.center_id, nom: c?.name ?? '', code: c?.tag ?? '', anneeScolaire: l.annee_scolaire });
  }
  return out.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

export interface LienDeGarderie {
  id: string;
  centerId: string;
  ecole: string;
  codeEcole: string;
  anneeScolaire: number;
  libelle: string;
  jeton: string;
  code: string | null;
  expireLe: string;
  creeLe: string;
  dernierUsageLe: string | null;
  pointages: number;
  essaisCode: number;
}

export async function lireLesLiens(): Promise<LienDeGarderie[]> {
  const { data, error } = await db
    .from('garderie_liens')
    .select('id, center_id, annee_scolaire, libelle, jeton, code, expire_le, cree_le, dernier_usage_le, pointages, essais_code, centers(name, tag)')
    .is('revoque_le', null)
    .order('cree_le');
  if (error) throw error;
  type Lu = {
    id: string; center_id: string; annee_scolaire: number; libelle: string; jeton: string; code: string | null;
    expire_le: string; cree_le: string; dernier_usage_le: string | null; pointages: number; essais_code: number;
    centers: { name: string; tag: string } | { name: string; tag: string }[] | null;
  };
  return ((data ?? []) as unknown as Lu[]).map((l) => {
    const c = Array.isArray(l.centers) ? l.centers[0] : l.centers;
    return {
      id: l.id,
      centerId: l.center_id,
      ecole: c?.name ?? '',
      codeEcole: c?.tag ?? '',
      anneeScolaire: l.annee_scolaire,
      libelle: l.libelle,
      jeton: l.jeton,
      code: l.code,
      expireLe: l.expire_le,
      creeLe: l.cree_le,
      dernierUsageLe: l.dernier_usage_le,
      pointages: l.pointages,
      essaisCode: l.essais_code,
    };
  });
}

export async function creerLeLien(centerId: string, code: string | null): Promise<void> {
  const { error } = await db.rpc('garderie_liens_creer', { p_center_id: centerId, p_code: code });
  if (error) throw error;
}

export async function revoquerLeLien(id: string): Promise<void> {
  const { error } = await db.rpc('garderie_lien_revoquer', { p_lien_id: id });
  if (error) throw error;
}
